import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wifi } from 'lucide-react';
import { useDeviceStore } from '../../stores/deviceStore';
import { useUIStore } from '../../stores/uiStore';
import { mockAPI } from '../../api/mockApi';
import { DeviceIcon } from '../common/DeviceIcon';

const PAIRING_DURATION = 60;

export function PairingScreen() {
  const navigate = useNavigate();
  const { fetchDevices } = useDeviceStore();
  const { addToast } = useUIStore();

  const [isPairing, setIsPairing] = useState(false);
  const [timeLeft, setTimeLeft] = useState(PAIRING_DURATION);
  const [foundDevices, setFoundDevices] = useState<any[]>([]);

  const startPairing = useCallback(async () => {
    setIsPairing(true);
    setTimeLeft(PAIRING_DURATION);
    setFoundDevices([]);
    await mockAPI.setPairingMode(1, true);
  }, []);

  const stopPairing = useCallback(async () => {
    setIsPairing(false);
    await mockAPI.setPairingMode(1, false);
  }, []);

  useEffect(() => {
    let timer: any;
    let deviceCheckInterval: any;

    if (isPairing && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            stopPairing();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      deviceCheckInterval = setInterval(async () => {
        const devices = await mockAPI.getPairingDeviceList('test');
        setFoundDevices((devices as any)?.devices || []);
      }, 3000);
    }

    return () => {
      clearInterval(timer);
      clearInterval(deviceCheckInterval);
    };
  }, [isPairing, timeLeft, stopPairing]);

  const handleConnect = async (device: any) => {
    await mockAPI.setDevicePairing(1, device.deviceId, device.deviceName, 'Гостиная');
    await fetchDevices();
    addToast(`${device.deviceName} добавлено!`, 'success');
    navigate('/devices');
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                stopPairing();
                navigate('/devices');
              }}
              className="p-2 -ml-2 rounded-full hover:bg-gray-100"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </button>
            
            <h1 className="text-xl font-bold text-gray-900">Добавить устройство</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {!isPairing ? (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Wifi className="w-12 h-12 text-blue-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Готово к подключению</h2>
            <p className="text-gray-500 mb-8 max-w-sm mx-auto">
              Переведите устройство в режим сопряжения и нажмите кнопку ниже
            </p>
            
            <button
              onClick={startPairing}
              className="px-8 py-4 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600"
            >
              Начать поиск
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <div className="relative w-32 h-32 mx-auto mb-4">
                <div className="absolute inset-0 rounded-full border-4 border-blue-200 border-t-blue-500 animate-spin"></div>
                <div className="absolute inset-4 rounded-full bg-blue-50 flex items-center justify-center">
                  <span className="text-2xl font-bold text-blue-600">{formatTime(timeLeft)}</span>
                </div>
              </div>
              
              <p className="text-gray-500">Поиск устройств...</p>
              
              <button
                onClick={stopPairing}
                className="mt-4 text-red-500 hover:underline"
              >
                Отмена
              </button>
            </div>

            {foundDevices.length > 0 && (
              <div className="space-y-3">
                <h2 className="font-semibold text-gray-900 mb-3">Найденные устройства</h2>
                
                {foundDevices.map((device) => (
                  <div
                    key={device.deviceId}
                    className="bg-white rounded-xl p-4 shadow-sm flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <DeviceIcon type={device.type} isOnline={true} size="sm" />
                      
                      <div>
                        <p className="font-medium text-gray-900">{device.deviceName}</p>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleConnect(device)}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium"
                    >
                      Подключить
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
